import React, { useMemo, useState } from "react";
import Grid from "@material-ui/core/Grid";
import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import * as Highcharts from "highcharts";
import { formatterCurrency } from "../../../../utils/formatacaoNumeros";
import { useAlturaDisponivel } from "../hooks/useAlturaDisponivel";
import { acionavelPorTeclado } from "../componentes/acionavel";
import { BORDA, FONTE, FUNDO_CARTAO, FUNDO_SUAVE, MEDIDAS_INDICADOR, SUAVE, estilos } from "../estilos/tokens";
import { inteiro, moeda, percentual } from "../formatadores";
import Grafico from "../componentes/Grafico";
import TotalRosca from "../componentes/TotalRosca";
import { PALETA_CLARA, Paleta } from "../../../../styles/theme/paletaCores";
import { usePaletaAtiva } from "../../../../styles/theme/usePaletaAtiva";
import { coresPorRanking } from "./coresOperadoras";

export interface Operadora {
  name: string;
  y: number;
}

export type FormatoIndicador = "MOEDA" | "INTEIRO" | "PERCENTUAL" | "TEXTO";

export interface Indicador {
  titulo: string;
  valor: number;
  destaque: boolean;
  formato?: FormatoIndicador;
  descricao?: string;
  texto?: string;
  detalhe?: string;
}

export interface IndicadorFormatado {
  sigla: string;
  texto: string;
}

export const formatarIndicador = (indicador: Indicador): IndicadorFormatado => {
  switch (indicador.formato) {
    case "INTEIRO":
      return { sigla: "", texto: inteiro(indicador.valor) };
    case "TEXTO":
      return { sigla: "", texto: indicador.texto || "-" };
    case "PERCENTUAL":
      // O valor ja chega em pontos percentuais, entao nao pode passar por Intl percent, que multiplica por cem
      return { sigla: "", texto: moeda(indicador.valor) + "%" };
    default:
      return { sigla: "R$", texto: moeda(indicador.valor) };
  }
};

export const textoIndicador = (indicador: Indicador): string => {
  const formatado = formatarIndicador(indicador);
  return formatado.sigla ? formatado.sigla + " " + formatado.texto : formatado.texto;
};

interface Props {
  titulo: string;
  operadoras: Operadora[];
  indicadores: Indicador[];
  detalhes?: React.ReactNode;
}

/**
 * Maior resto: arredondar cada fatia isoladamente fecha em 99,9% ou 100,1%.
 */
export const percentuaisQueSomamCem = (valores: number[], casas = 2): number[] => {
  const escala = Math.pow(10, casas);
  const positivo = (valor: number) => (valor > 0 ? valor : 0);
  const total = valores.reduce((soma, valor) => soma + positivo(valor), 0);
  if (total <= 0) {
    return valores.map(() => 0);
  }
  const exatos = valores.map((valor) => (positivo(valor) / total) * 100 * escala);
  const pisos = exatos.map((valor) => Math.floor(valor));
  let sobra = Math.round(100 * escala) - pisos.reduce((soma, valor) => soma + valor, 0);
  const porResto = exatos
    .map((valor, indice) => ({ indice, resto: valor - pisos[indice] }))
    .sort((a, b) => b.resto - a.resto);
  for (let i = 0; i < porResto.length && sobra > 0; i++, sobra--) {
    pisos[porResto[i].indice] += 1;
  }
  return pisos.map((valor) => valor / escala);
};

export const CartaoResumo = ({ rotulo, valor }: { rotulo: string; valor: number }) => (
  <div style={{ ...estilos.cartao, height: "auto", padding: "20px 16px", textAlign: "center" }}>
    <div style={estilos.resumoRotulo}>{rotulo}</div>
    <div
      style={{
        ...estilos.resumoValor,
        display: "flex",
        alignItems: "baseline",
        justifyContent: "center",
        flexWrap: "wrap",
        columnGap: 5,
      }}
    >
      <span style={estilos.indicadorSigla}>R$</span>
      {moeda(valor)}
    </div>
  </div>
);

const CABECALHO_CARTAO = 110;
const RODAPE_DETALHES = 80;
const ALTURA_MAX_ROSCA = 700;
const ALTURA_MIN_ROSCA = 200;
const ALTURA_ROSCA_SEM_MEDIDA = 390;

export const alturaRosca = (alturaDisponivel: number) => {
  if (alturaDisponivel <= 0) {
    return ALTURA_ROSCA_SEM_MEDIDA;
  }
  const espaco = alturaDisponivel - CABECALHO_CARTAO;
  return Math.min(Math.max(espaco, ALTURA_MIN_ROSCA), ALTURA_MAX_ROSCA);
};

export const montaOpcoesGrafico = (
  operadoras: Operadora[],
  altura: number,
  paleta: Paleta = PALETA_CLARA
): Highcharts.Options => {
  const corDe = coresPorRanking(operadoras, paleta);
  return {
    credits: { enabled: false },
    chart: { type: "pie", height: altura, backgroundColor: "transparent", margin: [0, 0, 0, 0] },
    title: { text: "" },
    tooltip: {
      formatter(this: any) {
        return `<b>${this.point.name}</b><br/>${formatterCurrency(this.y)} (${percentual(this.percentage)})`;
      },
      backgroundColor: paleta.fundoCartao,
      borderColor: paleta.borda,
      style: { fontFamily: FONTE, color: paleta.navy },
    },
    legend: { enabled: false },
    plotOptions: {
      pie: {
        innerSize: "68%",
        size: "94%",
        borderWidth: 3,
        borderColor: paleta.fundoCartao,
        dataLabels: { enabled: false },
        states: { hover: { brightness: 0.08, halo: { size: 6, opacity: 0.15 } } },
      },
    },
    series: [
      {
        type: "pie",
        name: "Valor",
        data: operadoras.map((o) => ({ ...o, color: corDe(o.name) })),
      },
    ],
  };
};

const estilosTextoIndicador = {
  ...estilos.indicadorValor,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical" as "vertical",
  overflow: "hidden",
  whiteSpace: "normal" as "normal",
  overflowWrap: "anywhere" as "anywhere",
  lineHeight: 1.2,
};

const estilosDetalhe = {
  fontFamily: FONTE,
  fontSize: MEDIDAS_INDICADOR.detalhe,
  fontWeight: 500,
  color: SUAVE,
  marginTop: 4,
  whiteSpace: "nowrap" as "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const CartaoIndicadorPainel = ({ indicador }: { indicador: Indicador }) => {
  const formatado = formatarIndicador(indicador);
  const cartao = (
    <div
      data-teste="cartao-indicador"
      style={{
        ...estilos.cartao,
        padding: MEDIDAS_INDICADOR.padding,
        borderRadius: MEDIDAS_INDICADOR.raio,
        background: FUNDO_CARTAO,
        borderColor: BORDA,
        cursor: indicador.descricao ? "help" : undefined,
      }}
    >
      <div style={{ ...estilos.indicadorTitulo, display: "flex", alignItems: "center", gap: 6 }}>
        {indicador.titulo}
        {indicador.descricao ? (
          <InfoOutlined data-teste="ajuda-indicador" aria-hidden style={{ fontSize: 17, color: SUAVE }} />
        ) : null}
      </div>
      {indicador.formato === "TEXTO" ? (
        <div data-teste="texto-indicador" title={formatado.texto} style={estilosTextoIndicador}>
          {formatado.texto}
        </div>
      ) : (
        <div style={estilos.indicadorValor}>
          {formatado.sigla ? <span style={estilos.indicadorSigla}>{formatado.sigla}</span> : null}
          {formatado.texto}
        </div>
      )}
      {indicador.detalhe ? (
        <div data-teste="detalhe-indicador" title={indicador.detalhe} style={estilosDetalhe}>
          {indicador.detalhe}
        </div>
      ) : null}
    </div>
  );
  return indicador.descricao ? (
    <Tooltip
      title={
        <span data-teste="texto-ajuda-indicador" style={estilos.textoAjudaIndicador}>
          {indicador.descricao}
        </span>
      }
      arrow
      placement="top"
    >
      {cartao}
    </Tooltip>
  ) : (
    cartao
  );
};

const montaCartoes = (indicadores: Indicador[]) => (
  <Grid container spacing={2} style={{ marginBottom: 26 }}>
    {indicadores.map((i) => (
      <Grid item xs={12} sm={6} md={3} key={i.titulo}>
        <CartaoIndicadorPainel indicador={i} />
      </Grid>
    ))}
  </Grid>
);

const PainelOperadoras = ({ titulo, operadoras, indicadores, detalhes }: Props) => {
  const total = operadoras.reduce((soma, o) => soma + o.y, 0);
  const positivas = useMemo(() => operadoras.filter((o) => o.y > 0), [operadoras]);
  const maiorValor = positivas.length > 0 ? Math.max(...positivas.map((o) => o.y)) : 0;
  const { referencia: refCartao, altura: alturaDisponivel } = useAlturaDisponivel();
  const [detalhesAbertos, setDetalhesAbertos] = useState(false);
  const [idDetalhes] = useState(() => `painel-detalhes-${Math.random().toString(36).slice(2, 10)}`);

  const temDetalhes = !!detalhes;
  const paleta = usePaletaAtiva();
  const opcoesGrafico = useMemo(
    () => montaOpcoesGrafico(positivas, alturaRosca(alturaDisponivel - (temDetalhes ? RODAPE_DETALHES : 0)), paleta),
    [positivas, alturaDisponivel, temDetalhes, paleta]
  );

  const corDe = useMemo(() => coresPorRanking(operadoras, paleta), [operadoras, paleta]);
  const percentuais = useMemo(() => percentuaisQueSomamCem(operadoras.map((o) => o.y)), [operadoras]);
  const proporcao = (valor: number) => (maiorValor > 0 ? Math.max(0, (valor / maiorValor) * 100) : 0);

  return (
    <div>
      {montaCartoes(indicadores)}

      <div
        ref={refCartao}
        style={{
          ...estilos.cartao,
          minHeight: alturaDisponivel || undefined,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={estilos.tituloCartao}>{titulo}</div>
        <Grid container alignItems="center" style={{ padding: "10px 30px 30px", flex: 1 }}>
          <Grid item xs={12} md={6}>
            {positivas.length > 0 ? (
              <Grafico opcoes={opcoesGrafico} centro={(diametroFuro) => <TotalRosca total={total} diametroFuro={diametroFuro} />} />
            ) : (
              <div style={estilos.semDados}>Sem valores para exibir no gráfico.</div>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <div style={{ padding: "0 10px" }}>
              {operadoras.map((o, indice) => (
                <div key={`${o.name}-${indice}`} style={{ marginBottom: 23 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 8 }}>
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: corDe(o.name),
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ ...estilos.legendaNome, flex: 1, minWidth: 0 }} title={o.name}>
                      {o.name}
                    </span>
                    <span style={estilos.legendaValor}>{formatterCurrency(o.y)}</span>
                    <span style={estilos.legendaPercentual}>{percentual(percentuais[indice])}</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: FUNDO_SUAVE, overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${proporcao(o.y)}%`,
                        height: "100%",
                        borderRadius: 4,
                        background: corDe(o.name),
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Grid>
        </Grid>

        {detalhes && (
          <>
            <Collapse in={detalhesAbertos} unmountOnExit>
              <div id={idDetalhes} style={{ borderTop: `1px solid ${BORDA}`, padding: "24px 30px" }}>
                {detalhes}
              </div>
            </Collapse>

            <div style={estilos.rodapeDetalhes}>
              <div
                style={estilos.botaoDetalhes}
                aria-expanded={detalhesAbertos}
                aria-controls={detalhesAbertos ? idDetalhes : undefined}
                {...acionavelPorTeclado(() => setDetalhesAbertos((aberto) => !aberto))}
              >
                {detalhesAbertos ? <KeyboardArrowUp style={{ fontSize: 24 }} /> : <KeyboardArrowDown style={{ fontSize: 24 }} />}
                <span style={estilos.rodapeDetalhesTexto}>
                  {detalhesAbertos ? "Ocultar detalhes" : "Exibir detalhes"}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PainelOperadoras;
